import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { subscriptionApi, type Plan } from '../api/subscription';

// Same gap as FeedbackPage.tsx: no SMTP on the embedded desktop API, so a
// POST /subscription/upgrade-request there would silently vanish. Fall back
// to mailto: instead, matching the existing desktop-email-gap pattern.
const IS_DESKTOP_APP = import.meta.env.VITE_USE_HASH_ROUTER === 'true';

const PLANS: Array<{
  key: Plan;
  name: string;
  price: string;
  renewal: string;
  color: string;
  badge?: string;
  features: string[];
  locked: string[];
}> = [
  {
    key: 'TRIAL',
    name: 'Free Trial',
    price: '₹0',
    renewal: '7 days only',
    color: '#64748b',
    features: [
      '20 invoices / LR during trial',
      'Tax Invoice & Bill of Supply only',
      '1 user',
      'Standard invoice template',
      'Reports — view only',
      'Cloud app access for 7 days',
    ],
    locked: ['WhatsApp reminders', 'All invoice types', 'GST report exports', 'E-Invoicing', 'Data migration'],
  },
  {
    key: 'BASIC',
    name: 'Basic',
    price: '₹1,800',
    renewal: '₹1,800 / year',
    color: '#0ea5e9',
    features: [
      'Unlimited invoices',
      'All invoice types',
      '1 user',
      'Standard templates (Classic + Modern)',
      'GSTR-1, GSTR-3B reports',
      'Sales & Purchase Register',
      'Party Ledger, HSN Summary',
      'Receivable Ageing, Cash Ledger',
      'WhatsApp reminders',
    ],
    locked: ['Multi-user (2+)', 'Stock Report', 'Profit & Loss', 'Bank Reconciliation', 'E-Invoicing', 'E-Way Bill', 'GSTR-2A API Sync'],
  },
  {
    key: 'PRIMA',
    name: 'Prima',
    price: '₹2,500',
    renewal: '₹2,500 / year',
    color: '#8b5cf6',
    badge: 'Most Popular',
    features: [
      'Unlimited invoices — all types',
      '2 users',
      'All invoice templates',
      'All GST reports',
      'Stock Report',
      'Profit & Loss',
      'WhatsApp reminders',
      'Priority support',
      'One-time data migration',
    ],
    locked: ['Bank Reconciliation', 'Balance Sheet', 'Loan & Capital Tracking', 'E-Invoicing', 'E-Way Bill', 'GSTR-2A API Sync', 'Manufacturing Module', 'Up to 10 users'],
  },
  {
    key: 'GOLD',
    name: 'Gold',
    price: '₹7,000',
    renewal: '₹6,000 / year',
    color: '#f59e0b',
    badge: 'Enterprise',
    features: [
      'Unlimited invoices — all types',
      '10 users',
      'All templates (5 + 100 more)',
      'All reports + Balance Sheet',
      'Bank Statement Reconciliation',
      'Loan & Capital Tracking',
      'E-Invoicing & E-Way Bill',
      'GSTR-2A/2B API Integration',
      'Manufacturing module (WIP tracking)',
      'WhatsApp reminders',
      'Priority support (same day)',
      'Unlimited data migration',
    ],
    locked: [],
  },
];

function CheckIcon({ color }: { color: string }) {
  return <span style={{ color, fontWeight: 700, marginRight: '0.4rem' }}>✓</span>;
}

function LockIcon() {
  return <span style={{ color: '#cbd5e1', marginRight: '0.4rem' }}>🔒</span>;
}

export default function SubscriptionPage() {
  const { data: sub, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: subscriptionApi.get,
    staleTime: 60_000,
  });
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  if (isLoading) return <div className="page"><div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Loading…</div></div>;

  const currentPlan = sub?.plan ?? 'TRIAL';

  async function handleEmailUs() {
    if (IS_DESKTOP_APP) {
      // No permanent "sent" state here — this just re-opens the user's own mail
      // client each click, unlike the real backend send used elsewhere.
      window.location.href = 'mailto:support@coreledger.in?subject=CoreLedger%20Plan%20Upgrade&body=Hi%2C%20I%27d%20like%20to%20upgrade%20my%20CoreLedger%20plan.';
      return;
    }
    setEmailStatus('sending');
    try {
      await subscriptionApi.requestUpgrade();
      setEmailStatus('sent');
    } catch {
      setEmailStatus('error');
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Subscription & Plans</h1>
      </div>

      {/* Current plan status card */}
      {sub && (
        <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
              Current Plan
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text)' }}>{sub.planName}</div>
            {sub.plan === 'TRIAL' && sub.trialDaysLeft !== null && (
              <div style={{ fontSize: '0.83rem', color: sub.trialDaysLeft <= 2 ? 'var(--danger)' : 'var(--text-muted)', marginTop: '0.2rem' }}>
                {sub.isExpired ? '⛔ Trial expired' : `⏱ ${sub.trialDaysLeft} day${sub.trialDaysLeft === 1 ? '' : 's'} left in trial`}
              </div>
            )}
            {sub.plan !== 'TRIAL' && sub.expiresAt && (
              <div style={{ fontSize: '0.83rem', color: sub.isExpired ? 'var(--danger)' : 'var(--text-muted)', marginTop: '0.2rem' }}>
                {sub.isExpired ? '⛔ Expired on ' : '✓ Valid until '}
                {new Date(sub.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '2rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>{sub.usage.invoices}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {sub.plan === 'TRIAL' ? `/ 20 invoices used` : 'invoices this month'}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--primary)' }}>{sub.usage.users}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>/ {sub.limits.usersMax} users</div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade contact note */}
      <div className="success-box" style={{ padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ fontSize: '0.875rem' }}>
          <strong>Ready to upgrade?</strong> Contact us — we'll activate your plan instantly.
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <a
            href="https://wa.me/919876543210?text=Hi%2C%20I%27d%20like%20to%20upgrade%20my%20CoreLedger%20plan."
            target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#25d366', color: 'white', border: 'none', borderRadius: 6, padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none', cursor: 'pointer' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp Us
          </a>
          <button
            type="button"
            onClick={handleEmailUs}
            disabled={emailStatus === 'sending' || emailStatus === 'sent'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'var(--surface)', color: 'var(--success)', border: '1.5px solid var(--success)', borderRadius: 6, padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.85rem', cursor: emailStatus === 'sending' || emailStatus === 'sent' ? 'default' : 'pointer' }}
          >
            {emailStatus === 'sending' ? 'Sending…' : emailStatus === 'sent' ? '✓ Sent — we\'ll be in touch' : '✉ Email Us'}
          </button>
        </div>
        {emailStatus === 'error' && (
          <div style={{ width: '100%', fontSize: '0.8rem', color: 'var(--danger)' }}>
            Couldn't send that — please try again, or reach us directly at support@coreledger.in.
          </div>
        )}
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
        {PLANS.map(plan => {
          const isCurrent = plan.key === currentPlan;
          return (
            <div key={plan.key} style={{
              border: `2px solid ${isCurrent ? plan.color : 'var(--border)'}`,
              borderRadius: 12,
              padding: '1.5rem',
              background: isCurrent ? `${plan.color}08` : 'var(--surface)',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
            }}>
              {/* Badge */}
              {plan.badge && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: plan.color, color: 'white', fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.75rem', borderRadius: 99, whiteSpace: 'nowrap' }}>
                  {plan.badge}
                </div>
              )}
              {isCurrent && (
                <div style={{ position: 'absolute', top: 12, right: 12, background: plan.color, color: 'white', fontSize: '0.68rem', fontWeight: 700, padding: '0.15rem 0.6rem', borderRadius: 99 }}>
                  Current
                </div>
              )}

              {/* Plan name + price */}
              <div style={{ fontWeight: 800, fontSize: '1.15rem', color: plan.color, marginBottom: '0.25rem' }}>{plan.name}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>{plan.price}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>{plan.renewal}</div>

              {/* Included features */}
              <div style={{ flex: 1 }}>
                {plan.features.map(f => (
                  <div key={f} style={{ fontSize: '0.82rem', marginBottom: '0.3rem', display: 'flex', alignItems: 'flex-start' }}>
                    <CheckIcon color={plan.color} />{f}
                  </div>
                ))}
                {plan.locked.map(f => (
                  <div key={f} style={{ fontSize: '0.82rem', marginBottom: '0.3rem', display: 'flex', alignItems: 'flex-start', color: 'var(--text-muted)' }}>
                    <LockIcon />{f}
                  </div>
                ))}
              </div>

              {/* CTA */}
              {isCurrent ? (
                <div style={{ marginTop: '1.25rem', textAlign: 'center', fontSize: '0.8rem', color: plan.color, fontWeight: 700 }}>✓ Your current plan</div>
              ) : (
                <a
                  href={`https://wa.me/919876543210?text=Hi%2C%20I%27d%20like%20to%20upgrade%20to%20CoreLedger%20${encodeURIComponent(plan.name)}%20plan.`}
                  target="_blank" rel="noreferrer"
                  style={{ marginTop: '1.25rem', display: 'block', textAlign: 'center', background: plan.color, color: 'white', borderRadius: 6, padding: '0.55rem 1rem', fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none' }}
                >
                  Get {plan.name} →
                </a>
              )}
            </div>
          );
        })}
      </div>

      {/* Comparison note */}
      <div style={{ marginTop: '2rem', padding: '1.25rem', background: 'var(--bg-2)', borderRadius: 8, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
        <strong style={{ color: 'var(--text)' }}>Note:</strong> All plans are annual (paid once per year). Renewal price applies from the second year onwards.
        The Gold plan first-year fee is ₹7,000; renewal is ₹6,000/year. Data migration is a one-time service performed by our team.
      </div>
    </div>
  );
}
